import React from 'react';

import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import { constructQueryParamName } from './SearchPage.shared';
import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import BookingDateRangeFilter from './BookingDateRangeFilter/BookingDateRangeFilter';
import KeywordFilter from './KeywordFilter/KeywordFilter';
import PriceFilter from './PriceFilter/PriceFilter';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { parse } from '../../util/urlHelpers';
import { parseSelectFilterOptions } from '../../util/search';

// Helper: get enumOptions in a format that works as query parameter
const createFilterOptions = (options, counts = null, selectedValues = []) => {
  // return options.map(o => ({ key: `${o.value || o.option}`, label: o.label }));
  return options
    .map(o => {
      const key = `${o.value || o.option}`;
      const count = counts?.[key];
      const isSelected = selectedValues.includes(key);
      const hasCount = typeof count === 'number';
      const shouldShow = o.show && (!counts || count > 0 || isSelected);

      return shouldShow
        ? {
            key,
            label: hasCount ? `${o.label} (${count})` : o.label,
          }
        : null;
    })
    .filter(o => o);
};
// return options.map(o => (o.show ? { key: `${o.option}`, label: o.label } : null)).filter(o => o);
/**
 * FilterComponent is used to map configured filter types
 * to actual filter components
 */
const FilterComponent = props => {
  const {
    idPrefix,
    config,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    marketplaceCurrency,
    intl,
    languageInCookie,
    facetCounts,
    ...rest
  } = props;
  // Note: config can be either
  // - listingFields config or
  // - default filter config
  // They both have 'key' and 'schemaType' included.
  const { key, schemaType } = config;
  if (!key) return null;
  const { liveEdit, showAsPopup } = rest;

  const useHistoryPush = liveEdit || showAsPopup;
  const prefix = idPrefix || 'SearchPage';
  const componentId = `${prefix}.${key.toLowerCase()}`;
  const name = key.replace(/\s+/g, '-').toLowerCase();
  const history = useHistory();
  const location = history?.location;
  const pathname = location?.pathname;
  const search = parse(location?.search);
  // Default filters: price, keywords, dates
  switch (schemaType) {
    case 'price': {
      const { min, max, step } = config;
      return (
        <PriceFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.priceLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={min}
          max={max}
          step={step}
          marketplaceCurrency={marketplaceCurrency}
          {...rest}
        />
      );
    }
    case 'keywords':
      return (
        <KeywordFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.keywordsLabel' })}
          name={name}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );
    case 'dates': {
      const { dateRangeMode } = config;
      const isNightlyMode = dateRangeMode === 'night';
      return (
        <BookingDateRangeFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.datesLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          minimumNights={isNightlyMode ? 1 : 0}
          {...rest}
        />
      );
    }
    case 'seller': {
      const queryParamNames = [key];
      const selectedValues = parseSelectFilterOptions(
        initialValues(queryParamNames, liveEdit)?.[key] || ''
      );
      const options = createFilterOptions(
        (config.options || []).map(option => ({
          value: option.key,
          label: option.labelTranslationKey
            ? intl.formatMessage({ id: option.labelTranslationKey })
            : option.label,
          show: true,
        })),
        facetCounts?.[key],
        selectedValues
      );

      return (
        <SelectSingleFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.sellerLabel' })}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSelect={getHandleChangedValueFn(useHistoryPush)}
          options={options}
          pathname={pathname}
          languageInCookie={languageInCookie}
          {...rest}
        />
      );
    }
  }

  // Custom extended data filters
  switch (schemaType) {
    case SCHEMA_TYPE_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const queryParamNames = [constructQueryParamName(key, scope)];
      const queryParamName = queryParamNames[0];
      const options = createFilterOptions(
        enumOptions,
        facetCounts?.[queryParamName],
        parseSelectFilterOptions(initialValues(queryParamNames, liveEdit)?.[queryParamName] || '')
      );

      if (options?.length === 0) return null;

      return filterConfig.filterType === 'SelectSingleFilter' ? (
        <SelectSingleFilter
          id={componentId}
          label={filterConfig.label}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSelect={getHandleChangedValueFn(useHistoryPush)}
          options={options}
          pathname={pathname}
          languageInCookie={languageInCookie}
          {...rest}
        />
      ) : (
        <SelectMultipleFilter
          id={componentId}
          label={filterConfig.label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={options}
          schemaType={schemaType}
          search={search}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_MULTI_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, searchMode } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      const queryParamName = queryParamNames[0];
      const options = createFilterOptions(
        enumOptions,
        facetCounts?.[queryParamName],
        parseSelectFilterOptions(initialValues(queryParamNames, liveEdit)?.[queryParamName] || '')
      );

      if (options?.length === 0) return null;

      return (
        <SelectMultipleFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={options}
          schemaType={schemaType}
          searchMode={searchMode}
          {...rest}
        />
      );
    }
    default:
      return null;
  }
};

export default FilterComponent;
